import React, { useState } from 'react';
import { Button, H2, Icon } from '@tviklax/ui-components';
import { Section, FormGrid, FormGroup, TextArea, Label } from './styles';
import { OrderFormData, RemittanceTrancheFormData } from './types';

const MILESTONE_OPTIONS = [
  'Advance Payment',
  'Intermediate Payment',
  'BL Copy',
  'Final Payment',
  'Shipment Release'
];

interface DeliveryScheduleProps {
  formData: OrderFormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onRemittanceTrancheChange: (index: number, field: keyof RemittanceTrancheFormData, value: string | number) => void;
  onAddRemittanceTranche: () => void;
  onRemoveRemittanceTranche: (index: number) => void;
  orderTotal: number;
}

export const DeliverySchedule: React.FC<DeliveryScheduleProps> = ({
  formData,
  onChange,
  onRemittanceTrancheChange,
  onAddRemittanceTranche,
  onRemoveRemittanceTranche,
  orderTotal,
}) => {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const totalPercentage = formData.remittanceTranches.reduce((sum, t) => sum + t.percentage, 0);
  const percentageError = Math.abs(totalPercentage - 100) > 0.01;

  // Validation helpers
  const getDateError = (index: number): string | null => {
    const tranche = formData.remittanceTranches[index];
    if (!tranche.dueDate) return 'Date required';

    // Check if date is after previous tranche
    if (index > 0) {
      const prevDate = new Date(formData.remittanceTranches[index - 1].dueDate);
      const currentDate = new Date(tranche.dueDate);
      if (currentDate <= prevDate) {
        return 'Must be after previous date';
      }
    }

    return null;
  };

  return (
    <Section>
      <H2 icon={<Icon name="CreditCard" size={20} color="#f86400" />}>Payment Terms</H2>
      <FormGrid>
        <FormGroup $span={4}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <Label style={{ marginBottom: 0 }}>Remittance Schedule</Label>
            <Button type="button" variant="outline" size="sm" onClick={onAddRemittanceTranche}>
              Add Tranche
            </Button>
          </div>

          {/* Remittance Grid */}
          <div style={{
            overflowX: 'auto',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            marginBottom: '12px',
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '13px',
            }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#475569', minWidth: '60px' }}>Tranche</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#475569', minWidth: '100px' }}>Percentage</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#475569', minWidth: '140px' }}>Due Date</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#475569', minWidth: '160px' }}>Milestone</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#475569', minWidth: '140px' }}>Amount</th>
                  <th style={{ padding: '10px', textAlign: 'center', fontWeight: 600, color: '#475569', minWidth: '60px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {formData.remittanceTranches.map((tranche, index) => {
                  const amount = ((orderTotal || 0) * (tranche.percentage || 0)) / 100;
                  const dateError = getDateError(index);
                  const isRowFocused = focusedIndex === index;

                  return (
                    <tr
                      key={index}
                      onMouseEnter={() => setFocusedIndex(index)}
                      onMouseLeave={() => setFocusedIndex(null)}
                      style={{
                        background: isRowFocused ? '#f0f9ff' : index % 2 === 0 ? '#fff' : '#f9fafb',
                        borderBottom: '1px solid #e2e8f0',
                        transition: 'background 0.15s',
                      }}
                    >
                      <td style={{ padding: '10px', fontWeight: 600, color: '#334155' }}>#{index + 1}</td>
                      <td style={{ padding: '10px' }}>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={tranche.percentage}
                          onChange={(e) => onRemittanceTrancheChange(index, 'percentage', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            fontSize: '13px',
                          }}
                        />
                      </td>
                      <td style={{ padding: '10px' }}>
                        <div>
                          <input
                            type="date"
                            value={tranche.dueDate}
                            onChange={(e) => onRemittanceTrancheChange(index, 'dueDate', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: dateError ? '1px solid #ef4444' : '1px solid #cbd5e1',
                              borderRadius: '6px',
                              fontSize: '13px',
                            }}
                          />
                          {dateError && (
                            <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '2px' }}>{dateError}</div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '10px' }}>
                        <select
                          value={tranche.milestone}
                          onChange={(e) => onRemittanceTrancheChange(index, 'milestone', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            fontSize: '13px',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                          }}
                        >
                          <option value="">Select milestone...</option>
                          {MILESTONE_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '10px', fontWeight: 600, color: '#047857' }}>
                        {formData.currency} {amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemoveRemittanceTranche(index)}
                          disabled={formData.remittanceTranches.length === 1}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Total percentage display */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px 12px',
            background: percentageError ? '#fef2f2' : '#f0fdf4',
            border: `1px solid ${percentageError ? '#fecaca' : '#bbf7d0'}`,
            borderRadius: '8px',
            marginBottom: '12px',
          }}>
            <span style={{ fontWeight: 600, color: '#334155' }}>Total Percentage</span>
            <span style={{
              fontWeight: 700,
              color: percentageError ? '#dc2626' : '#16a34a',
            }}>
              {totalPercentage.toFixed(2)}%
            </span>
          </div>

          {percentageError && (
            <div style={{
              padding: '10px 12px',
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#991b1b',
              fontSize: '12px',
              marginBottom: '12px',
            }}>
              ⚠️ Remittance percentages must total 100%. Current: {totalPercentage.toFixed(2)}%
            </div>
          )}

          <span style={{ fontSize: '11px', color: '#94a3b8' }}>
            Define each remittance milestone explicitly. Dates must be sequential and percentages must total 100%.
          </span>
        </FormGroup>

        <FormGroup $span={4}>
          <Label style={{ display: 'block', marginBottom: '8px' }}>Delivery Instructions</Label>
          <TextArea
            name="specialInstructions"
            value={formData.specialInstructions}
            onChange={onChange}
            style={{ minHeight: '100px' }}
            placeholder="e.g., Delivery time windows, fragile notes, etc."
          />
          <span style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Provide any specific requirements for final delivery</span>
        </FormGroup>
      </FormGrid>
    </Section>
  );
};
